<template>
    <section class="mb-48">

        <application-title :title="$t('applications.documents')">
            <application-button
                :condition="canManage"
                :title="$t('general.add_subject', {subject: $t('applications.documents')})"
                icon="add"
                @click="addDocuments"
            />
        </application-title>

        <loading v-if="fetchingDocuments"></loading>
        <ul v-else-if="documents.length" class="rounded-6 max-h-480 overflow-y-auto">
            <li
                v-for="document in documents"
                :key="document.id"
                class="odd:bg-primary-5 relative flex items-center"
            >
                <button :disabled="!canManage"
                        class="px-8 py-5 font-semibold ml-6 rounded-4 text-primary hover:bg-primary-5 hover:text-primary"
                        role="button"
                        @click="editDocument = document.id; $refs['edit-' + document.id][0].startEdit(); ">
                    <svg-icon class="w-16 h-16" type="edit"/>
                </button>

                <div v-show="editDocument === document.id" class="p-16 mr-48 flex-grow break-words">
                    <click-edit-input
                        :ref="'edit-' + document.id"
                        :value="document.name || document.original_name "
                        class="mr-auto font-semibold group-hover:text-primary trans-ease"
                        full-width
                        @input="editDocument = null; editDocumentTitle($event, document.id)"
                    ></click-edit-input>
                </div>
                <a v-show="editDocument !== document.id"
                   :download="!extensions.includes(document.extension) ? document.original_name : null"
                   :href="document.document_url" @click="openDocument($event, document)"
                   :target="extensions.includes(document.extension) ? '_blank' : null"
                   class="text-left flex group items-center p-16 flex-grow overflow-hidden"
                >
                    <span class="w-4/5 mr-auto flex items-center">
                        <span class="font-semibold group-hover:text-primary trans-ease break-words">
                            {{ document.name || document.original_name }}
                        </span>

                        <!--                        TODO VIRUS SCANNER UI: UNCOMMENT -->
                        <!--                        <span class="ml-6">-->
                        <!--                            <Tooltip v-if="!document.scanned_at"-->
                        <!--                                     :content="$t('applications.documents-unscanned')">-->
                        <!--                                <svg-icon class="w-16 h-16 text-semantic-warning" type="warning"/>-->
                        <!--                            </Tooltip>-->
                        <!--                            <Tooltip v-else-if="document.safe"-->
                        <!--                                     :content="$t('applications.documents-scanned-safe')">-->
                        <!--                                <svg-icon class="w-16 h-16 text-secondary" type="check-circle"/>-->
                        <!--                            </Tooltip>-->
                        <!--                            <Tooltip v-else-->
                        <!--                                     :content="$t('applications.documents-scanned-infected')">-->
                        <!--                                <svg-icon class="w-16 h-16 text-semantic-error" type="warning"/>-->
                        <!--                            </Tooltip>-->
                        <!--                        </span>-->
                    </span>

                    <div class="pl-16">
                        <div class="ml-auto h-28 w-32 flex items-center justify-center rounded-6 text-primary-80 trans-ease group-hover:text-primary mr-32">
                            <svg-icon class="max-w-16 max-h-16" type="download"></svg-icon>
                        </div>
                    </div>
                </a>

                <div v-if="canManage" class="absolute right-0 inset-y-0 mr-16 flex items-center pointer-events-none">
                    <button class="text-primary-80 hover:text-semantic-error cursor-pointer trans-ease pointer-events-auto h-32 w-32 flex items-center justify-center rounded-6" @click="confirmDelete(document)">
                        <svg-icon class="max-w-20 max-h-20" type="delete"></svg-icon>
                    </button>
                </div>

            </li>
        </ul>

        <popup-modal-confirm
            v-if="canManage && deleting"
            :confirmText="$t('general.delete_subject', {subject: $t('general.doc')})"
            :message="$t('general.confirm_delete_subject', {subject: deleting.original_name})"
            show-exclamation
            :working="performingDeleteRequest"
            @close="cancelDelete"
            @confirm="deleteDocument(deleting)"
        >
        </popup-modal-confirm>

        <popup-modal-confirm
            v-if="openingUnsafeDocument"
            :title="!openingUnsafeDocument.scanned_at ? $t('applications.documents-open-unscanned-file-title') : $t('applications.documents-open-infected-file-title')"
            :message="!openingUnsafeDocument.scanned_at ? $t('applications.documents-open-unscanned-file-message') : $t('applications.documents-open-infected-file-message')"
            :confirmText="!openingUnsafeDocument.scanned_at ? $t('applications.documents-open-unscanned-file') : $t('applications.documents-open-infected-file')"
            @confirm="forceOpenDocument"
            @close="openingUnsafeDocument = null"
            show-exclamation
            :toggle-text="!openingUnsafeDocument.scanned_at ? undefined : $t('applications.documents-open-infected-file-confirm')"
        ></popup-modal-confirm>

        <document-uploader
            v-if="canManage && addingDocuments"
            :application="application"
            @close="stopAddingDocuments"
        />

    </section>
</template>

<script>
import Loading from './ui/Loading.vue';
import SimpleButton from './ui/SimpleButton.vue';
import * as documents from '../services/DocumentService';
import Document from '../models/Document';
import {normalizeJson, openableFileExtensionsInWebBrowser} from '../../shared/helpers';
import SvgIcon from './ui/SvgIcon.vue';
import PopupModalConfirm from './ui/PopupModalConfirm.vue';
import DocumentUploader from './ui/DocumentUploader.vue';
import ClickEditInput from './forms/ClickEditInput';
import Tooltip from "./navigation/Tooltip.vue";
import ApplicationButton from "./ui/ApplicationButton.vue";
import ApplicationTitle from "./applications/ApplicationTitle.vue";
import {useRepo} from "pinia-orm";

export default {
    components: {
        ApplicationTitle,
        ApplicationButton,
        Tooltip,
        ClickEditInput,
        'loading': Loading,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
        'document-uploader': DocumentUploader,
    },
    props: {
        application: Object,
        canManage: Boolean,
    },
    data: function () {
        return {
            fetchingDocuments: true,
            deleting: null,
            performingDeleteRequest: false,
            addingDocuments: false,
            extensions: openableFileExtensionsInWebBrowser,
            editDocument: null,
            openingUnsafeDocument: null
        };
    },
    computed: {
        documents() {
            return useRepo(Document)
                .where('application_id', this.application.id)
                .orderBy('created_at')
                .get();
        },
    },
    methods: {
        fetchDocuments() {
            this.fetchingDocuments = true;

            documents.index(this.application.id).then(result => {
                return useRepo(Document).save(normalizeJson(result.data));
            }).then(inserts => {
                this.fetchingDocuments = false;
            });
        },
        confirmDelete(document) {
            this.deleting = document;
        },
        cancelDelete() {
            this.deleting = null;
        },
        deleteDocument(document) {
            this.performingDeleteRequest = true;

            documents.destroy(document.id).then(() => {
                useRepo(Document).destroy(document.id);
            }).then(() => {
                this.deleting = null;
                this.performingDeleteRequest = false;
            });
        },
        addDocuments() {
            this.addingDocuments = true;
        },
        stopAddingDocuments() {
            this.addingDocuments = false;
        },

        editDocumentTitle(event, docId) {
            // Update the section in our store to change it immediately.
            useRepo(Document)
                .whereId(docId)
                .update({name: event});

            // Update name in db
            documents.update(docId, {data: {attributes: {name: event}}});
        },

        openDocument(event, document) {
            // TODO VIRUS SCANNER UI: Remove return;
            return;

            if (document.safe)
                return;

            event.preventDefault();
            this.openingUnsafeDocument = document;
        },
        forceOpenDocument() {
            const doc = this.openingUnsafeDocument;

            // Open the document in a new tab.
            window.open(doc.document_url, this.extensions.includes(doc.extension) ? '_blank' : undefined);
            this.openingUnsafeDocument = null;
        }
    },
    mounted() {
        this.fetchDocuments();
    },
};
</script>
